import { createApi } from '@reduxjs/toolkit/query/react';

import { REACT_APP_BE_API_URL } from '#shared';

import {
  asQueryFnResult,
  fetchWithAuthBaseQuery,
  requestJsonWithAuth,
} from '#shared/api';

import { parseAlertSnapshot } from './schemas';

import type { AlertSnapshot } from '../model/types';

const paths = { alerts: 'api/alerts' } as const;

/**
 * The alert state snapshot.
 *
 * ONE cache entry, no query argument: the snapshot is always the whole current
 * active set, so there is no view to scope and no window to widen.
 *
 * No `merge`, deliberately. A refetch must REPLACE: an alert the new snapshot
 * omits has cleared, and merging would keep it on screen forever — which is the
 * missed-`cleared` case this endpoint exists to heal (377:E027).
 *
 * No `tagTypes`, matching `domainEvents.api.ts`: the cache is maintained by the
 * socket through `util.updateQueryData`, never by tag invalidation (377:E034).
 * For the same reason there is no `api/queries/` wrapper — `usePollingQuery`
 * skips while the socket is connected, and this data is *driven* by that socket
 * plus its own heartbeat tick.
 *
 * `GET /api/alerts` answers 503 when the backend stream is disabled, by design:
 * "a disabled stream must fail loudly, not answer 'no alerts'". That surfaces as
 * `isError`, which is what lets the page distinguish unavailable from all-clear
 * (377:E026).
 */
export const alertsApi = createApi({
  reducerPath: 'alertsApi',
  baseQuery: fetchWithAuthBaseQuery,
  endpoints: builder => ({
    getAlerts: builder.query<AlertSnapshot, void>({
      /**
       * A `queryFn`, not a `query`, because the status has to be checked.
       *
       * The shared base query goes through `fetchWithAuthHeaders`, which calls
       * `res.json()` whatever the status — "no body parsing, no status
       * checking". A 503 body would therefore arrive as DATA, `parseAlertSnapshot`
       * would find no `alerts` array and answer an empty snapshot, and an empty
       * snapshot renders as ALL CLEAR. That is precisely the reading the backend
       * returns 503 to prevent (377:E026): "a disabled stream must fail loudly,
       * not answer 'no alerts'".
       *
       * `requestJsonWithAuth` exists for exactly this — "callers that must not
       * mistake an error body for a result" — and `asQueryFnResult` gives it the
       * base query's own error shape and session recovery, so this is not a
       * second auth path.
       */
      queryFn: () =>
        asQueryFnResult(async () =>
          parseAlertSnapshot(
            await requestJsonWithAuth(`${REACT_APP_BE_API_URL}${paths.alerts}`),
          ),
        ),
    }),
  }),
});

export const { useGetAlertsQuery } = alertsApi;
